/**
 Copyright Highway9 Networks Inc.
 */
import moment from "moment";
import { decodeJWTToken, encrypt } from "~/helpers/utils";
import { UserInfo, UserSessionInfo } from "~/types/user";
import APIService from "./APIServices";

const sessionsAPI = new APIService<UserSessionInfo>("sessions");

export const authenticationService = {
  login,
  logout,
  getSession,
  isOperator,
  isTenant,
  isPartner,
  validateCurrentUser,
  getSessionInfo,
  get currentUserValue() {
    return getCurrentUser();
  },
  get currentUserInfo() {
    return getCurrentUserInfo();
  },
  isSystemEngineer,
  isTenantAdmin,
  canViewPlanningMap
};

function getCurrentUser() {
  if (typeof window !== "undefined") {
    const state = localStorage.getItem("currentUser");
    if (state) {
      return JSON.parse(state) as UserSessionInfo;
    } else {
      return null;
    }
  }
  return null;
}

function getCurrentUserInfo() {
  if (typeof window !== "undefined") {
    const state = localStorage.getItem("userInfo");
    if (state) {
      return JSON.parse(state) as UserInfo;
    } else {
      return null;
    }
  }
  return null;
}

async function login(username: string, password: string, publicKey: string, otpCode?: string) {
  const body: { email: string; password: string; otp?: string } = {
    email: username,
    password: encrypt(password, publicKey) || "",
  };

  if (otpCode) {
    body["otp"] = otpCode;
  }

  // Unauthenticated login needs status-based error handling that APIService.post does not expose.
  const requestOptions: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(body),
  };

  try {
    const response = await fetch(`${sessionsAPI.url}/login`, requestOptions);
    const res = await response.text();
    const data = res && JSON.parse(res);
    if (!response.ok) {
      if ([401].indexOf(response.status) !== -1) {
        // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
        authenticationService.logout();
        window.location.reload();
      }

      const error = (data && data.message) || response.statusText;
      return Promise.reject(error);
    }
    // store user details and jwt token in local storage to keep user logged in between page refreshes
    localStorage.setItem("currentUser", JSON.stringify(data));
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function getSession() {
  // Cookie-based session check — callers expect a raw Response (status === 200).
  const requestOptions: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
  };

  return fetch(`${sessionsAPI.url}/session`, requestOptions)
    .then(async (response) => {
      if (!response.ok) {
        throw response;
      }
      const user = await response.text();
      // store user details and jwt token in local storage to keep user logged in between page refreshes
      console.log("user: " + user);
      localStorage.setItem("currentUser", user);
      return response;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

async function getSessionInfo(email: string, redirectUrl = "/") {
  // Needs redirect: "manual" and no auth header — keep a targeted fetch on the APIService base URL.
  const res = await fetch(`${sessionsAPI.url}/login/query?uri=${redirectUrl}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
    redirect: "manual",
    body: JSON.stringify({ email })
  });

  if (!res.ok) {
    throw new Error("Invalid user");
  }

  const data = await res.json();
  if (data.authType === "PASSWORD") {
    return {
      authType: "PASSWORD",
      authKey: data.authKey,
      otpRequired: data.otpRequired === true
    }
  } else if (data.authType === "SAML") {
    return {
      authType: "SAML",
      redirectUrl: data.redirectUrl
    }
  }
}

function logout() {
  // remove user from local storage to log user out
  localStorage.clear();
  sessionStorage.clear();
  // TODO: Good to add a Logout URL to clear the auth cookie.
  // Auth cookie is short lived so any logout attempt made when the cookie is active
  // will lead the the browser spinning till the cookie expires in thill then the /sessions/session api will continue to succeed.
  window.location.reload();
}

function isOperator() {
  return Boolean(authenticationService.currentUserValue?.isOperator);
}

// Single source of truth for backend role-name strings. Every raw role literal in the app
// (e.g. "OPERATOR", "TENANT_ADMIN") should resolve through this map instead of being
// retyped, so a typo fails at compile time instead of silently never matching.
export const ROLE_NAMES = {
  OPERATOR: "OPERATOR",
  OPERATOR_READONLY: "OPERATOR_READONLY",
  TENANT_ADMIN: "TENANT_ADMIN",
  READONLY_TENANT: "READONLY_TENANT",
  TENANT_MOBILE_CENTER_ADMIN: "TENANT_MOBILE_CENTER_ADMIN",
  PARTNER_MSP_ADMIN: "PARTNER_MSP_ADMIN",
  PARTNER_MSP_MONITOR: "PARTNER_MSP_MONITOR",
  RAN_PARTNER_ADMIN: "RAN_PARTNER_ADMIN",
  RAN_PARTNER_READONLY: "RAN_PARTNER_READONLY",
  SYSTEM_ENGINEER_ADMIN: "SYSTEM_ENGINEER_ADMIN",
  RAN_KPI_ONLY: "RAN_KPI_ONLY",
  AI_RAN_PARTNER: "AI_RAN_PARTNER",
  PARTNER_MOBILE_CENTER_ADMIN: "PARTNER_MOBILE_CENTER_ADMIN",
  MOBILE_APP_READONLY: "MOBILE_APP_READONLY",
} as const;

export type RoleName = (typeof ROLE_NAMES)[keyof typeof ROLE_NAMES];

// Role-string equivalent of the `isOperator` session flag — used only where a role NAME
// (not the real user's session) needs to be checked, e.g. resolving the Role Switcher's
// masked role in `useCurrentRole`.
export const OPERATOR_ROLES: RoleName[] = [ROLE_NAMES.OPERATOR, ROLE_NAMES.OPERATOR_READONLY];

export const PARTNER_ROLES: RoleName[] = [
  ROLE_NAMES.PARTNER_MSP_ADMIN,
  ROLE_NAMES.PARTNER_MSP_MONITOR,
  ROLE_NAMES.RAN_PARTNER_ADMIN,
  ROLE_NAMES.RAN_PARTNER_READONLY,
  ROLE_NAMES.SYSTEM_ENGINEER_ADMIN,
  ROLE_NAMES.RAN_KPI_ONLY,
  ROLE_NAMES.AI_RAN_PARTNER,
  ROLE_NAMES.PARTNER_MOBILE_CENTER_ADMIN,
];

export const TENANT_ROLES: RoleName[] = [ROLE_NAMES.TENANT_ADMIN, ROLE_NAMES.READONLY_TENANT, ROLE_NAMES.TENANT_MOBILE_CENTER_ADMIN];

export const SYSTEM_ENGINEER_ROLES: RoleName[] = [ROLE_NAMES.SYSTEM_ENGINEER_ADMIN, ROLE_NAMES.PARTNER_MSP_ADMIN, ROLE_NAMES.PARTNER_MSP_MONITOR];

function isPartner() {
  return authenticationService?.currentUserInfo?.roles.some((item: string) => PARTNER_ROLES.includes(item as RoleName))
}

function isTenant() {
  return authenticationService?.currentUserInfo?.roles.some((item: string) => TENANT_ROLES.includes(item as RoleName))
}

function isSystemEngineer() {
  return authenticationService?.currentUserInfo?.roles.some((item: string) => SYSTEM_ENGINEER_ROLES.includes(item as RoleName))
}

function isTenantAdmin() {
  return authenticationService?.currentUserInfo?.roles.some((item: string) => item === ROLE_NAMES.TENANT_ADMIN);
}

// CI-260: Planning Map tab is shown to every role except the read-only/auditor roles
// (Tenant Auditor, Radio Installer, KPI Auditor).
export const PLANNING_MAP_EXCLUDED_ROLES: RoleName[] = [ROLE_NAMES.READONLY_TENANT, ROLE_NAMES.MOBILE_APP_READONLY, ROLE_NAMES.RAN_KPI_ONLY];
function canViewPlanningMap() {
  if (isOperator()) return true;
  const roles = authenticationService?.currentUserInfo?.roles ?? [];
  return roles.length > 0 && !roles.some((role: string) => PLANNING_MAP_EXCLUDED_ROLES.includes(role as RoleName));
}

function validateCurrentUser() {
  try {
    const currentUserText = localStorage.getItem("currentUser");
    if (currentUserText) {
      const currentUser = JSON.parse(currentUserText);
      if (currentUser.token) {
        const token = currentUser.token;
        const tokenParts = token.split(".");
        if (tokenParts.length > 2) {
          const json = decodeJWTToken(token);
          if (!json || (json.exp <= moment.now() / 1000)) {
            console.log("deleting user token ...: " + json?.exp);
            localStorage.removeItem("currentUser");
          }
        } else {
          // Invalid token format
          localStorage.removeItem("currentUser");
        }
      }
    }
  } catch {
    // If token parsing fails, treat as invalid and clear it
    localStorage.removeItem("currentUser");
  }
}
